import * as RadixSlider from "@radix-ui/react-slider";
import { ReactNode, useMemo } from "react";
import { cx } from "../../utils/cx";
import styles from "./Slider.module.scss";

export type SliderType = "basic" | "with-marks" | "range";

export interface SliderMark {
    value: number;
    label?: ReactNode;
}

export interface SliderProps {
    /** Single-thumb (basic/with-marks) or two-thumb range slider. */
    type?: SliderType;
    /** Minimum value. */
    min?: number;
    /** Maximum value. */
    max?: number;
    /** Step increment. */
    step?: number;
    /** Controlled value. Number for single, tuple for range. */
    value?: number | [number, number];
    /** Uncontrolled initial value. */
    defaultValue?: number | [number, number];
    /** Change handler matching controlled value shape. */
    onValueChange?: (value: number | [number, number]) => void;
    /** Disable interactions. */
    disabled?: boolean;
    /**
     * Value labels per Figma matrix:
     * - `basic`: min (left) and max (right) under the track.
     * - `range`: pill under each thumb with the current value.
     * - `with-marks`: use `marks[].label`; this flag does not duplicate marks.
     */
    showValueLabels?: boolean;
    /** Optional marks rendered under the track. */
    marks?: SliderMark[];
    /** Width behavior. */
    fullWidth?: boolean;
    /** Extra class name. */
    className?: string;
    /** aria-label for single slider or first thumb in range. */
    "aria-label"?: string;
    /** aria-label for second thumb in range. */
    "aria-label-2"?: string;
}

const asArray = (
    type: SliderType,
    value: SliderProps["value"] | SliderProps["defaultValue"] | undefined,
    min: number,
    max: number,
) => {
    if (Array.isArray(value)) return value;
    if (typeof value === "number") return [value];
    return type === "range" ? [min, max] : [min];
};

/**
 * Treez DS — Slider (Wave 1).
 *
 * Figma source: `Slider` page (`7826:41655`).
 *
 * Supports the 3 matrix types from Figma:
 * - `basic`      (single thumb)
 * - `with-marks` (single thumb + marks)
 * - `range`      (two thumbs)
 *
 * Plus optional value labels and disabled states.
 */
export const Slider = ({
    type = "basic",
    min = 0,
    max = 100,
    step = 1,
    value,
    defaultValue,
    onValueChange,
    disabled = false,
    showValueLabels = false,
    marks,
    fullWidth = true,
    className,
    "aria-label": ariaLabel = "Slider value",
    "aria-label-2": ariaLabel2 = "Maximum slider value",
}: SliderProps) => {
    /* Storybook controls can pass numeric args as strings. Coerce so math
       for mark positions never becomes NaN (which stacks all marks at 0). */
    const minNum = Number.isFinite(Number(min)) ? Number(min) : 0;
    const maxNum = Number.isFinite(Number(max)) ? Number(max) : 100;
    const stepNum =
        Number.isFinite(Number(step)) && Number(step) > 0 ? Number(step) : 1;

    const resolvedMarks = useMemo(() => {
        if (marks && marks.length > 0) return marks;
        if (type !== "with-marks") return [];
        const quarter = (maxNum - minNum) / 4;
        return [
            { value: minNum },
            { value: minNum + quarter },
            { value: minNum + quarter * 2 },
            { value: minNum + quarter * 3 },
            { value: maxNum },
        ];
    }, [marks, type, minNum, maxNum]);

    const isControlled = value !== undefined;
    const resolvedValue = asArray(type, value ?? defaultValue, minNum, maxNum);

    return (
        <div
            className={cx(
                styles.root,
                fullWidth && styles.fullWidth,
                type === "range" &&
                    showValueLabels &&
                    styles.rootRangeValueLabels,
                className,
            )}
            data-disabled={disabled || undefined}
            data-type={type}
            data-ui="slider"
        >
            <RadixSlider.Root
                aria-label={ariaLabel}
                className={styles.slider}
                defaultValue={isControlled ? undefined : resolvedValue}
                disabled={disabled}
                max={maxNum}
                min={minNum}
                onValueChange={next =>
                    onValueChange?.(
                        type === "range"
                            ? ([next[0], next[1]] as [number, number])
                            : next[0],
                    )
                }
                step={stepNum}
                value={isControlled ? resolvedValue : undefined}
            >
                <RadixSlider.Track className={styles.track}>
                    <RadixSlider.Range className={styles.range} />
                </RadixSlider.Track>

                {resolvedValue.map((thumbValue, idx) => (
                    <RadixSlider.Thumb
                        aria-label={idx === 0 ? ariaLabel : ariaLabel2}
                        className={styles.thumb}
                        key={idx}
                    >
                        {showValueLabels && type === "range" && (
                            <span className={styles.valueLabelThumb}>
                                {Math.round(thumbValue)}
                            </span>
                        )}
                    </RadixSlider.Thumb>
                ))}
            </RadixSlider.Root>

            {type === "basic" && showValueLabels && (
                <div className={styles.trackEndLabels}>
                    <span className={styles.trackEndLabel}>{minNum}</span>
                    <span className={styles.trackEndLabel}>{maxNum}</span>
                </div>
            )}

            {resolvedMarks.length > 0 && (
                <div className={styles.marks}>
                    {resolvedMarks.map(mark => {
                        const pct =
                            ((mark.value - minNum) / (maxNum - minNum || 1)) *
                            100;
                        return (
                            <div
                                className={styles.mark}
                                key={`${mark.value}-${String(
                                    mark.label ?? "",
                                )}`}
                                style={{ left: `${pct}%` }}
                            >
                                <span className={styles.markTick} />
                                {mark.label !== undefined && (
                                    <span className={styles.markLabel}>
                                        {mark.label}
                                    </span>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};
